import {
  SHARE_MODES,
  captureWeights,
  isEqualSplit,
  toCents,
} from "./money";
import { ENTRY_TYPES } from "./entry-type";
import { isSplitMode, splitError } from "./split";

export const EXPENSE_TYPE = ENTRY_TYPES;

export const isPayment = (expense) => expense?.type === EXPENSE_TYPE.payment;

export const isIncome = (expense) => expense?.type === EXPENSE_TYPE.income;

// Form values turn ids into strings.
export const participantName = (participants, id, fallback = "Unbekannt") => {
  if (id === null || id === undefined || id === "") return fallback;
  return (
    (participants ?? []).find((p) => Number(p.id) === Number(id))?.name ??
    fallback
  );
};

export const PAYMENT_TITLE = "Zahlung";

export const paymentTitleOf = (payment) => {
  const name = typeof payment?.name === "string" ? payment.name.trim() : "";
  return name && name !== PAYMENT_TITLE ? name : "";
};

export const paymentSubtitle = (payment) => {
  const title = paymentTitleOf(payment);
  return title ? `${PAYMENT_TITLE} · ${title}` : PAYMENT_TITLE;
};

export const NOTE_MAX_LENGTH = 255;

export const noteLength = (text) => [...(text ?? "")].length;

export const noteOf = (expense) => {
  const text =
    typeof expense?.description === "string"
      ? expense.description.replace(/\s+/g, " ").trim()
      : "";
  return text || undefined;
};

export const descriptionChange = (input, expense) => {
  const next = (input ?? "").trim();
  if (!expense?.id) return next || undefined;
  const previous = (expense.description ?? "").trim();
  return next === previous ? undefined : next || null;
};

const optionalFields = ({ spentAt, tags, description }) => ({
  ...(spentAt == null ? {} : { spent_at: spentAt }),
  ...(Array.isArray(tags) ? { tags: [...tags] } : {}),
  ...(description === undefined ? {} : { description }),
});

const sameId = (a, b) => `${a}` === `${b}`;

export const defaultPayerId = (me, participants = []) =>
  me && participants.some((p) => sameId(p.id, me.id)) ? me.id : null;

export const toPaymentPayload = ({
  fromId,
  toId,
  amount,
  title = "",
  spentAt,
  description,
}) => ({
  type: EXPENSE_TYPE.payment,
  title: title ?? "",
  amount,
  from: { id: fromId },
  receivers: [{ id: toId, share: amount }],
  ...optionalFields({ spentAt, description }),
});

export const toExpensePayload = ({
  type = EXPENSE_TYPE.expense,
  title,
  amount,
  payerId,
  receivers = [],
  spentAt,
  tags,
  description,
  splitMode,
}) => {
  const withSplit = isSplitMode(splitMode);
  return {
    type: type === EXPENSE_TYPE.income ? EXPENSE_TYPE.income : EXPENSE_TYPE.expense,
    title,
    amount,
    from: { id: payerId },
    receivers: receivers.map(({ id, share, value }) =>
      withSplit ? { id, share, split_value: value } : { id, share },
    ),
    split_mode: withSplit ? splitMode : null,
    ...optionalFields({ spentAt, tags, description }),
  };
};

export const splitOfExpense = (expense) => {
  const rows = expense?.receivers ?? [];
  const receivers = rows.map((r) => ({
    id: r.participant_id,
    share: toCents(r.absolute_share_in_decimals),
    locked: false,
  }));
  const mode = expense?.split_mode;
  const values = rows.map((r) => Number(r.relative_share_in_decimals));
  if (isSplitMode(mode) && splitError(mode, values) === null) {
    return {
      mode,
      receivers: receivers.map((r, index) => ({ ...r, value: values[index] })),
    };
  }
  return {
    mode: isEqualSplit(receivers) ? SHARE_MODES.equal : SHARE_MODES.amounts,
    receivers: captureWeights(receivers),
  };
};

export const tagNamesOf = (expense) =>
  (expense?.tags ?? []).map((tag) => tag.name);
